<script lang="ts">
  import { deviceLayout, deviceMeta } from "$lib/serial/connection";
  import { dev } from "$app/environment";
  import ActionSelector from "$lib/components/layout/ActionSelector.svelte";
  import { get } from "svelte/store";
  import KeyboardKey from "$lib/components/layout/KeyboardKey.svelte";
  import { getContext, mount, unmount } from "svelte";
  import type { VisualLayoutConfig } from "./visual-layout.js";
  import { changes, ChangeType, layout, settings } from "$lib/undo-redo";
  import { fly } from "svelte/transition";
  import { expoOut } from "svelte/easing";
  import { activeLayer, activeProfile } from "$lib/serial/connection";
  import type {
    CompiledLayout,
    CompiledLayoutKey,
  } from "$lib/assets/layouts/layout.d.ts";

  const { scale, margin, strokeWidth, fontSize, iconFontSize } =
    getContext<VisualLayoutConfig>("visual-layout-config");

  if (dev) {
    // you have absolutely no idea what a difference this makes for performance
    console.assert(scale % 1 === 0, "Scale must be an integer");
    console.assert((scale / 2) % 1 === 0, "Scale must be divisible by 2");
    console.assert(strokeWidth % 1 === 0, "Stroke must be an integer");
    console.assert(margin % 1 === 0, "Margin must be an integer");
    console.assert(fontSize % 1 === 0, "Font size must be an integer");
    console.assert(iconFontSize % 1 === 0, "Icon font size must be an integer");
  }

  let { layoutInfo }: { layoutInfo: CompiledLayout } = $props();

  function getCenter(key: CompiledLayoutKey): [x: number, y: number] {
    return [key.pos[0] + key.size[0] / 2, key.pos[1] + key.size[1] / 2];
  }

  function getDistance(a: CompiledLayoutKey, b: CompiledLayoutKey) {
    const x1 = a.pos[0] + margin;
    const y1 = a.pos[1] + margin;
    const x1b = x1 + a.size[0] - margin;
    const y1b = y1 + a.size[1] - margin;
    const x2 = b.pos[0] + margin;
    const y2 = b.pos[1] + margin;
    const x2b = x2 + b.size[0] - margin;
    const y2b = y2 + b.size[1] - margin;

    const left = x2b < x1;
    const right = x1b < x2;
    const bottom = y2b < y1;
    const top = y1b < y2;

    return top && left
      ? Math.sqrt((x1 - x2b) ** 2 + (y1b - y2) ** 2)
      : left && bottom
        ? Math.sqrt((x1 - x2b) ** 2 + (y1 - y2b) ** 2)
        : bottom && right
          ? Math.sqrt((x1b - x2) ** 2 + (y1 - y2b) ** 2)
          : right && top
            ? Math.sqrt((x1b - x2) ** 2 + (y1b - y2) ** 2)
            : left
              ? x1 - x2b
              : right
                ? x2 - x1b
                : bottom
                  ? y1 - y2b
                  : top
                    ? y2 - y1b
                    : 0;
  }

  function navigate(event: KeyboardEvent) {
    if (event.altKey || event.ctrlKey || event.shiftKey || event.metaKey)
      return;

    let wantedAngle: number;
    const angleThreshold = Math.PI;

    if (event.key === "ArrowUp") wantedAngle = Math.PI;
    else if (event.key === "ArrowDown") wantedAngle = 0;
    else if (event.key === "ArrowRight") wantedAngle = Math.PI / 2;
    else if (event.key === "ArrowLeft") wantedAngle = -Math.PI / 2;
    else return;

    event.preventDefault();
    if (!focusKey) (groupParent.firstChild as SVGGElement).focus();
    const [focusX, focusY] = getCenter(focusKey);

    let bestDistance = Infinity;
    let bestCandidate = 0;
    let isOptimalAngle = false;

    for (const [i, key] of layoutInfo.keys.entries()) {
      if (key === focusKey) continue;
      const [keyX, keyY] = getCenter(key);
      const deltaX = keyX - focusX;
      const deltaY = keyY - focusY;
      const angle = Math.atan2(deltaX, deltaY);
      const distance = getDistance(key, focusKey);

      const angleDelta = Math.abs(wantedAngle - angle);

      if (
        isOptimalAngle
          ? angleDelta > Number.EPSILON
          : angleDelta >= angleThreshold
      )
        continue;
      if (distance > bestDistance) continue;

      bestDistance = distance;
      bestCandidate = i;
      isOptimalAngle = angleDelta <= Number.EPSILON;
    }

    const node = groupParent.children.item(bestCandidate);
    if (node instanceof SVGGElement) {
      node.focus();
    }
  }

  function edit(index: number) {
    const keyInfo =
      layoutInfo.keys.find(({ id }) => id === index) ??
      layoutInfo.fixedKeys.find(({ id }) => id === index);
    if (!keyInfo) return;
    const clickedGroup = groupParent.querySelector(
      `g[data-id="${index}"]`,
    ) as SVGGElement;
    const nextAction =
      get(layout)[get(activeProfile)]![get(activeLayer)]?.[keyInfo.id];
    const currentAction =
      get(deviceLayout)[get(activeProfile)]![get(activeLayer)]?.[keyInfo.id];
    const component = mount(ActionSelector, {
      target: document.body,
      props: {
        currentAction,
        nextAction: nextAction?.isApplied ? undefined : nextAction?.action,
        onclose() {
          closed();
        },
        onselect(action) {
          changes.update((changes) => {
            changes.push([
              {
                type: ChangeType.Layout,
                id: keyInfo.id,
                layer: get(activeLayer),
                profile: get(activeProfile),
                action,
              },
            ]);
            return changes;
          });
          closed();
        },
      },
    });
    const dialog = document.querySelector("dialog > div") as HTMLDivElement;
    const backdrop = document.querySelector("dialog") as HTMLDialogElement;
    const dialogRect = dialog.getBoundingClientRect();
    const groupRect = clickedGroup.getBoundingClientRect();

    const scale = 0.5;
    const dialogScale = `${1 - scale * (1 - groupRect.width / dialogRect.width)} ${
      1 - scale * (1 - groupRect.height / dialogRect.height)
    }`;
    const dialogTranslate = `${scale * (groupRect.x - dialogRect.x)}px ${
      scale * (groupRect.y - dialogRect.y)
    }px`;

    const duration = 150;
    const options = { duration, easing: "ease" };
    const dialogAnimation = dialog.animate(
      [
        { scale: dialogScale, translate: dialogTranslate },
        { translate: "0 0", scale: "1" },
      ],
      options,
    );
    const backdropAnimation = backdrop.animate(
      [{ opacity: 0 }, { opacity: 1 }],
      options,
    );

    async function closed() {
      dialogAnimation.reverse();
      backdropAnimation.reverse();

      await dialogAnimation.finished;

      unmount(component);
    }
  }

  let focusKey: CompiledLayoutKey;
  let groupParent: SVGGElement;
  let rotationTarget: SVGCircleElement;
  let rotationSetting = $derived(
    $deviceMeta?.settings
      .find((it) => it.name === "misc")
      ?.items.find((it) => it.name === "device rotation"),
  );
  let settingRotation = $derived(
    rotationSetting
      ? ($settings[$activeProfile]?.[rotationSetting.id]?.value ?? 90)
      : 90,
  );

  let flippedSetting = $derived(
    $deviceMeta?.settings
      .find((it) => it.name === "misc")
      ?.items.find((it) => it.name === "device orientation"),
  );
  let flipped = $derived(
    flippedSetting
      ? $settings[$activeProfile]?.[flippedSetting.id]?.value !== 0
      : false,
  );

  let draggingRotation = $state(90);
  let isDragging = $state(false);
  let rotation = $derived(isDragging ? draggingRotation : settingRotation);
  let dragOffset = 0;

  function calcDragOffset(event: MouseEvent) {
    const offset = rotationTarget.getBoundingClientRect();
    const cx = offset.x + offset.width / 2;
    const cy = offset.y + offset.height / 2;
    const a = Math.atan2(event.x - cx, event.y - cy) * (180 / Math.PI) + 90;
    return flipped ? (a + 180) % 360 : a;
  }

  function toggleFlip() {
    changes.update((changes) => {
      changes.push([
        {
          type: ChangeType.Setting,
          id: flippedSetting!.id,
          setting: flipped ? 0 : 1,
          profile: get(activeProfile),
        },
      ]);
      return changes;
    });
  }

  function dragRotation(event: MouseEvent) {
    if (!isDragging) return;
    const value = Math.min(
      180,
      Math.max(0, Math.round(calcDragOffset(event) - dragOffset)),
    );
    if (draggingRotation !== value) {
      draggingRotation = value;
    }
  }

  function dragEnable(event: MouseEvent) {
    dragOffset = calcDragOffset(event) - rotation;
    draggingRotation = rotation;
    isDragging = true;
  }
  function dragDisable() {
    isDragging = false;
    if (settingRotation !== draggingRotation) {
      changes.update((changes) => {
        changes.push([
          {
            type: ChangeType.Setting,
            id: rotationSetting!.id,
            setting: draggingRotation,
            profile: get(activeProfile),
          },
        ]);
        return changes;
      });
    }
  }
</script>

<svelte:window on:keydown={navigate} />

<svg
  class="print"
  viewBox="0 {flipped ? margin * -3 : 0} {layoutInfo.size[0] *
    scale} {layoutInfo.size[1] * scale}"
  transition:fly={{ y: 48, easing: expoOut }}
  onmousemove={dragRotation}
  onmouseup={dragDisable}
>
  <g
    bind:this={groupParent}
    transform="rotate({flipped ? 180 : 0})"
    transform-origin="{(layoutInfo.rotationAnchor?.[0] ?? 0) *
      scale} {(layoutInfo.size[1] * scale) / 2}"
  >
    <g
      transform-origin="{(layoutInfo.rotationAnchor?.[0] ?? 0) *
        scale} {(layoutInfo.rotationAnchor?.[1] ?? 0) * scale}"
      transform="rotate({-(rotation - 90)})"
      class="group"
    >
      {#each layoutInfo.keys as key}
        <KeyboardKey
          {key}
          {flipped}
          onfocusin={() => (focusKey = key)}
          onclick={() => edit(key.id)}
          onkeypress={(event) => {
            if (event.key === "Enter") {
              edit(key.id);
            }
          }}
        />
      {/each}
      {#if rotationSetting}
        <rect
          role="button"
          tabindex="-1"
          onmousedown={dragEnable}
          ondblclick={toggleFlip}
          class="handle"
          x={(layoutInfo.size[0] * scale) / 2 - (0.5 * scale) / 2}
          y={layoutInfo.size[1] * scale + margin - 0.05 * scale}
          width={0.5 * scale}
          height={0.05 * scale}
          ry={0.025 * scale}
          fill="currentColor"
          stroke="currentColor"
          stroke-width={strokeWidth}
        />
        {#if isDragging}
          {@const x = (layoutInfo.size[0] * scale) / 2}
          {@const y = layoutInfo.size[1] * scale + margin * (flipped ? 2 : 3)}
          <text
            transition:fly={{ y: 2, easing: expoOut }}
            transform={flipped ? "rotate(180)" : undefined}
            transform-origin="{x} {y}"
            class="handle-label"
            text-anchor="middle"
            font-size={fontSize}
            fill="currentColor"
            {x}
            {y}>{rotation - 90}°</text
          >
        {/if}
      {/if}
    </g>
    <circle
      bind:this={rotationTarget}
      cx={(layoutInfo.rotationAnchor?.[0] ?? 0) * scale}
      cy={(layoutInfo.rotationAnchor?.[1] ?? 0) * scale}
      r="0"
    />

    <g
      transform="rotate({flipped ? 180 : 0})"
      transform-origin="{(layoutInfo.rotationAnchor?.[0] ?? 0) *
        scale} {(layoutInfo.rotationAnchor?.[1] ?? 0) * scale}"
    >
      {#each layoutInfo.fixedKeys as key}
        <KeyboardKey
          {key}
          onfocusin={() => (focusKey = key)}
          onclick={() => edit(key.id)}
          onkeypress={(event) => {
            if (event.key === "Enter") {
              edit(key.id);
            }
          }}
        />
      {/each}
    </g>
  </g>
</svg>

<style lang="scss">
  svg {
    grid-area: "d";
    width: calc(min(100%, 35cm));
    max-height: calc(100% - 170px);
    overflow: visible;
  }

  .handle {
    opacity: 0.3;
    transition: opacity 0.1s;
    cursor: grab;
  }

  .handle:hover {
    opacity: 0.5;
  }

  .handle:active {
    opacity: 1;
    cursor: grabbing;
  }

  .handle:focus {
    outline: none;
  }

  .handle-label {
    user-select: none;
  }
</style>
